import React, { useState, useEffect, useCallback } from 'react';
import { Search, Plus, Pencil, Trash2, Truck, BarChart3, Package, TrendingUp, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { adminAPI } from '../lib/api';
import { toast } from 'sonner';

const EMPTY_FORM = { code: '', name: '', phone: '', email: '', address: '', siret: '', notes: '' };

export const AdminSuppliers = () => {
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingSupplier, setEditingSupplier] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(false);

    const fetchSuppliers = useCallback(async () => {
        setLoading(true);
        try {
            const res = await adminAPI.getSuppliers({ search: search || undefined });
            setSuppliers(res.data.suppliers || []);
        } catch { toast.error('Erreur chargement fournisseurs'); }
        finally { setLoading(false); }
    }, [search]);

    const fetchStats = useCallback(async () => {
        setStatsLoading(true);
        try {
            const res = await adminAPI.getSupplierStats();
            setStats(res.data);
        } catch { toast.error('Erreur chargement statistiques'); }
        finally { setStatsLoading(false); }
    }, []);

    useEffect(() => { fetchSuppliers(); }, [fetchSuppliers]);

    const openCreate = () => { setEditingSupplier(null); setForm(EMPTY_FORM); setDialogOpen(true); };
    const openEdit = (s) => {
        setEditingSupplier(s);
        setForm({ 
            code: s.code, 
            company_name: s.company_name || s.name || '', 
            contact_name: s.contact_name || '', 
            phone_mobile: s.phone_mobile || s.phone || '', 
            email: s.email || '', 
            password: '', 
            address: s.address || '', 
            siret: s.siret || '', 
            notes: s.notes || '' 
        });
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (!form.code.trim() || !form.company_name.trim()) { toast.error('Code et nom societe obligatoires'); return; }
        if (form.code.trim().length < 2 || form.code.trim().length > 4) { toast.error('Le code doit faire 2-4 caracteres (ex: F01)'); return; }
        if (!editingSupplier && !form.email.trim()) { toast.error('Email obligatoire pour la creation'); return; }
        if (!editingSupplier && !form.password.trim()) { toast.error('Mot de passe obligatoire pour la creation'); return; }
        if (!editingSupplier && form.password.trim().length < 6) { toast.error('Mot de passe: 6 caracteres minimum'); return; }
        setSaving(true);
        try {
            const payload = { ...form };
            if (editingSupplier && !payload.password) delete payload.password;
            if (editingSupplier) {
                await adminAPI.updateSupplier(editingSupplier.id, payload);
                toast.success('Fournisseur mis a jour');
            } else {
                await adminAPI.createSupplier(payload);
                toast.success(`Fournisseur ${form.code} cree ! Identifiants : ${form.email} / ${form.password}`);
            }
            setDialogOpen(false);
            fetchSuppliers();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        } finally { setSaving(false); }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            await adminAPI.deleteSupplier(deleteTarget.id);
            toast.success('Fournisseur supprime');
            setDeleteTarget(null);
            fetchSuppliers();
        } catch { toast.error('Erreur suppression'); }
    };

    return (
        <div className="space-y-6" data-testid="admin-suppliers-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-heading text-2xl font-bold text-jungle">Fournisseurs</h1>
                    <p className="text-sm text-muted-foreground mt-1">{suppliers.length} fournisseur(s) — Code produit = prefixe fournisseur (ex: F01-BAN001)</p>
                </div>
                <Button onClick={openCreate} className="bg-leaf hover:bg-leaf/90 text-white" data-testid="add-supplier-btn">
                    <Plus className="w-4 h-4 mr-2" /> Ajouter
                </Button>
            </div>

            <Tabs defaultValue="list" onValueChange={(v) => { if (v === 'stats' && !stats) fetchStats(); }}>
                <TabsList>
                    <TabsTrigger value="list" data-testid="tab-list"><Truck className="w-4 h-4 mr-1" /> Liste</TabsTrigger>
                    <TabsTrigger value="stats" data-testid="tab-stats"><BarChart3 className="w-4 h-4 mr-1" /> Statistiques</TabsTrigger>
                </TabsList>

                <TabsContent value="list" className="space-y-4">
                    <div className="relative max-w-sm">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input placeholder="Rechercher..." value={search} onChange={(e) => setSearch(e.target.value)}
                            className="pl-10" data-testid="supplier-search" />
                    </div>

                    {loading ? (
                        <div className="text-center py-12 text-muted-foreground">Chargement...</div>
                    ) : suppliers.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">Aucun fournisseur</div>
                    ) : (
                        <div className="grid gap-3">
                            {suppliers.map(s => (
                                <Card key={s.id} className="hover:shadow-md transition-shadow" data-testid={`supplier-card-${s.id}`}>
                                    <CardContent className="p-4">
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="text-sm font-mono font-bold bg-leaf/15 text-jungle px-2.5 py-0.5 rounded-lg">{s.code}</span>
                                                    <h3 className="font-heading font-semibold text-jungle truncate">{s.company_name || s.name}</h3>
                                                </div>
                                                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground mt-1">
                                                    {s.contact_name && <span>{s.contact_name}</span>}
                                                    {(s.phone_mobile || s.phone) && <span>{s.phone_mobile || s.phone}</span>}
                                                    {s.email && <span>{s.email}</span>}
                                                    {s.address && <span>{s.address}</span>}
                                                    {s.siret && <span>SIRET: {s.siret}</span>}
                                                </div>
                                                {s.notes && <p className="text-xs text-muted-foreground/70 mt-1 italic">{s.notes}</p>}
                                            </div>
                                            <div className="flex items-center gap-1 flex-shrink-0">
                                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(s)} data-testid={`edit-supplier-${s.id}`}>
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => setDeleteTarget(s)} data-testid={`delete-supplier-${s.id}`}>
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="stats" className="space-y-6">
                    {statsLoading ? (
                        <div className="text-center py-12 text-muted-foreground">Chargement des statistiques...</div>
                    ) : !stats ? (
                        <div className="text-center py-12 text-muted-foreground">Cliquez sur l'onglet pour charger</div>
                    ) : (
                        <>
                            {/* Supplier summary cards */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {stats.suppliers_summary?.map(s => (
                                    <Card key={s.supplier_code} data-testid={`supplier-stat-${s.supplier_code}`}>
                                        <CardContent className="p-4">
                                            <div className="flex items-center gap-2 mb-3">
                                                <span className="text-sm font-mono font-bold bg-leaf/15 text-jungle px-2.5 py-0.5 rounded-lg">{s.supplier_code}</span>
                                                <span className="font-heading font-semibold text-jungle truncate">{s.supplier_name}</span>
                                            </div>
                                            <div className="grid grid-cols-3 gap-2 text-center">
                                                <div>
                                                    <p className="text-lg font-bold text-jungle">{s.total_products}</p>
                                                    <p className="text-[10px] text-muted-foreground">Produits</p>
                                                </div>
                                                <div>
                                                    <p className="text-lg font-bold text-jungle">{s.total_orders}</p>
                                                    <p className="text-[10px] text-muted-foreground">Commandes</p>
                                                </div>
                                                <div>
                                                    <p className="text-lg font-bold text-jungle">{s.total_qty_ordered}</p>
                                                    <p className="text-[10px] text-muted-foreground">Qte totale</p>
                                                </div>
                                            </div>
                                            {s.total_revenue > 0 && (
                                                <div className="flex justify-between text-xs font-medium mt-2 pt-2 border-t">
                                                    <span className="text-jungle/70">CA: {s.total_revenue.toFixed(2)} EUR</span>
                                                    {s.total_margin > 0 && (
                                                        <span className="text-emerald-600">Marge: {s.total_margin.toFixed(2)} EUR</span>
                                                    )}
                                                </div>
                                            )}
                                            {s.products?.length > 0 && (
                                                <div className="mt-3 border-t pt-2">
                                                    <p className="text-[10px] text-muted-foreground mb-1">Top produits :</p>
                                                    {s.products.slice(0, 5).map((p, i) => (
                                                        <div key={`${s.supplier_code}-prod-${i}`} className="flex justify-between text-xs">
                                                            <span className="truncate text-jungle/80">{p.product_code} {p.product_name}</span>
                                                            <span className="text-muted-foreground ml-2 flex-shrink-0">{p.total_qty}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>

                            {/* Top products table */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-jungle">
                                        <TrendingUp className="w-5 h-5" /> Tous les produits commandes par fournisseur
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {stats.top_products?.length === 0 ? (
                                        <p className="text-sm text-muted-foreground">Aucune commande pour l'instant</p>
                                    ) : (
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-sm" data-testid="stats-table">
                                                <thead>
                                                    <tr className="border-b text-left text-muted-foreground">
                                                        <th className="pb-2 font-medium">Code</th>
                                                        <th className="pb-2 font-medium">Produit</th>
                                                        <th className="pb-2 font-medium">Fournisseur</th>
                                                        <th className="pb-2 font-medium text-right">Qte</th>
                                                        <th className="pb-2 font-medium text-right">Prix vente</th>
                                                        <th className="pb-2 font-medium text-right">Prix achat</th>
                                                        <th className="pb-2 font-medium text-right">Marge/u</th>
                                                        <th className="pb-2 font-medium text-right">CA</th>
                                                        <th className="pb-2 font-medium text-right">Marge totale</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {stats.top_products?.map((p, i) => (
                                                        <tr key={p.product_id || i} className="border-b last:border-0">
                                                            <td className="py-2 font-mono text-xs">{p.product_code || '-'}</td>
                                                            <td className="py-2 font-medium text-jungle">{p.product_name}</td>
                                                            <td className="py-2">
                                                                {p.supplier_code !== '-' ? (
                                                                    <span className="inline-flex items-center gap-1">
                                                                        <span className="text-xs font-mono bg-leaf/15 text-jungle px-1.5 py-0.5 rounded">{p.supplier_code}</span>
                                                                        <span className="text-xs text-muted-foreground">{p.supplier_name}</span>
                                                                    </span>
                                                                ) : <span className="text-muted-foreground text-xs">Non assigne</span>}
                                                            </td>
                                                            <td className="py-2 text-right">{p.total_qty_ordered}</td>
                                                            <td className="py-2 text-right">{(p.sell_price || 0).toFixed(2)}</td>
                                                            <td className="py-2 text-right">{p.purchase_price > 0 ? p.purchase_price.toFixed(2) : <span className="text-muted-foreground">-</span>}</td>
                                                            <td className={`py-2 text-right font-medium ${p.margin_unit > 0 ? 'text-emerald-600' : p.margin_unit < 0 ? 'text-red-500' : ''}`}>
                                                                {p.margin_unit !== null ? `${p.margin_unit.toFixed(2)}` : '-'}
                                                            </td>
                                                            <td className="py-2 text-right">{(p.total_revenue || 0).toFixed(2)}</td>
                                                            <td className={`py-2 text-right font-bold ${p.total_margin > 0 ? 'text-emerald-600' : p.total_margin < 0 ? 'text-red-500' : ''}`}>
                                                                {p.total_margin !== null ? `${p.total_margin.toFixed(2)}` : '-'}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Unassigned products */}
                            {stats.unassigned_products?.length > 0 && (
                                <Card className="border-amber-200">
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2 text-amber-700 text-base">
                                            <AlertTriangle className="w-4 h-4" /> Produits sans fournisseur ({stats.unassigned_products.length})
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <p className="text-xs text-muted-foreground mb-3">
                                            Ces produits n'ont pas de code commencant par un code fournisseur (ex: F01-xxx). Modifiez leur code produit pour les associer.
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            {stats.unassigned_products.map((p, i) => (
                                                <span key={`unassigned-${p.product_name}-${i}`} className="text-xs bg-amber-50 text-amber-700 px-2 py-1 rounded">
                                                    {p.product_name} <span className="text-muted-foreground">({p.total_qty_ordered} unites)</span>
                                                </span>
                                            ))}
                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                        </>
                    )}
                </TabsContent>
            </Tabs>

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()}>
                    <DialogHeader>
                        <DialogTitle>{editingSupplier ? 'Modifier le fournisseur' : 'Nouveau fournisseur'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                                <Label>Code * <span className="text-xs text-muted-foreground">(2-4 car. ex: F01)</span></Label>
                                <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().slice(0, 4) })}
                                    placeholder="F01" maxLength={4} className="font-mono" disabled={!!editingSupplier} data-testid="supplier-code-input" />
                            </div>
                            <div className="space-y-1">
                                <Label>Nom societe *</Label>
                                <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                                    placeholder="HPS Fruits" data-testid="supplier-company-name-input" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                                <Label>Nom du contact</Label>
                                <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                                    placeholder="Jean Dupont" data-testid="supplier-contact-name-input" />
                            </div>
                            <div className="space-y-1">
                                <Label>Telephone mobile</Label>
                                <Input value={form.phone_mobile} onChange={(e) => setForm({ ...form, phone_mobile: e.target.value })}
                                    placeholder="06 12 34 56 78" data-testid="supplier-phone-input" />
                            </div>
                        </div>
                        <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                            <p className="text-xs font-semibold text-emerald-800 mb-2">Identifiants de connexion</p>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1">
                                    <Label>Email * {editingSupplier && <span className="text-xs text-muted-foreground">(non modifiable)</span>}</Label>
                                    <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                                        placeholder="contact@fournisseur.fr" type="email" disabled={!!editingSupplier} data-testid="supplier-email-input" />
                                </div>
                                <div className="space-y-1">
                                    <Label>Mot de passe {editingSupplier ? <span className="text-xs text-muted-foreground">(laisser vide pour garder)</span> : '*'}</Label>
                                    <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                                        placeholder={editingSupplier ? '••••••••' : 'Min. 6 caracteres'} type="text" data-testid="supplier-password-input" />
                                </div>
                            </div>
                        </div>
                        <div className="space-y-1">
                            <Label>Adresse</Label>
                            <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                                placeholder="Adresse" data-testid="supplier-address-input" />
                        </div>
                        <div className="space-y-1">
                            <Label>SIRET</Label>
                            <Input value={form.siret} onChange={(e) => setForm({ ...form, siret: e.target.value })}
                                placeholder="12345678901234" data-testid="supplier-siret-input" />
                        </div>
                        <div className="space-y-1">
                            <Label>Notes</Label>
                            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[60px] resize-y"
                                placeholder="Notes internes..." data-testid="supplier-notes-input" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
                        <Button onClick={handleSave} disabled={saving} className="bg-leaf hover:bg-leaf/90 text-white" data-testid="save-supplier-btn">
                            {saving ? 'Enregistrement...' : editingSupplier ? 'Modifier' : 'Creer'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirm */}
            <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer le fournisseur ?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Le fournisseur <strong>{deleteTarget?.company_name || deleteTarget?.name}</strong> ({deleteTarget?.code}) sera supprime. Les produits avec le prefixe {deleteTarget?.code} garderont leur code.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90" data-testid="confirm-delete-supplier">
                            Supprimer
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};
